import { cloneElement, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROJECT_TYPES = [
    "Residential — 3D Exterior",
    "Interior Design",
    "Walkthrough / Animation",
    "VR Experience",
    "Commercial Building",
    "Church / Institutional",
    "Renovation Visualization",
    "Other",
];

export default function LeadDialog({ trigger }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        project_type: "",
        budget: "",
        location: "",
        message: "",
    });

    const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.phone.trim()) {
            toast.error("Name, email and phone are required.");
            return;
        }
        setBusy(true);
        try {
            await axios.post(`${API}/leads`, { ...form, source: "consultation_dialog" });
            toast.success("Consultation booked. We'll reach out within 24 hours.");
            setOpen(false);
            setForm({
                name: "",
                email: "",
                phone: "",
                project_type: "",
                budget: "",
                location: "",
                message: "",
            });
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Failed to submit. Try again.");
        } finally {
            setBusy(false);
        }
    };

    const triggerEl = cloneElement(trigger, {
        onClick: (e) => {
            trigger.props.onClick?.(e);
            setOpen(true);
        },
    });

    return (
        <>
            {triggerEl}
            {open && (
                <div
                    className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm p-4 md:p-8 grid place-items-center"
                    onClick={() => setOpen(false)}
                    data-testid="lead-dialog"
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="relative w-full max-w-2xl rounded-2xl bg-card border hairline p-6 md:p-10 max-h-[90vh] overflow-y-auto"
                    >
                        <button
                            onClick={() => setOpen(false)}
                            data-testid="lead-dialog-close"
                            aria-label="Close"
                            className="absolute top-4 right-4 h-9 w-9 grid place-items-center rounded-full border hairline hover:border-gold transition"
                        >
                            <X className="h-4 w-4" />
                        </button>

                        <div className="flex items-center gap-3 mb-4">
                            <span className="divider-gold" />
                            <span className="uppercase tracking-[0.28em] text-xs text-gold font-medium">
                                Free Consultation
                            </span>
                        </div>
                        <h3 className="font-display text-2xl md:text-3xl leading-tight tracking-tight">
                            Let&apos;s design your dream project.
                        </h3>
                        <p className="mt-2 text-sm text-muted-foreground">
                            Tell us the essentials. We&apos;ll respond within 24 hours with next steps.
                        </p>

                        <form onSubmit={submit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
                            <Input label="Name *" testid="dlg-name" value={form.name} onChange={update("name")} required />
                            <Input label="Email *" testid="dlg-email" type="email" value={form.email} onChange={update("email")} required />
                            <Input label="Phone *" testid="dlg-phone" value={form.phone} onChange={update("phone")} required />
                            <Sel label="Project Type" testid="dlg-project-type" value={form.project_type} onChange={update("project_type")} />
                            <Input label="Budget" testid="dlg-budget" value={form.budget} onChange={update("budget")} placeholder="₹5 – 15 Lakhs" />
                            <Input label="Location" testid="dlg-location" value={form.location} onChange={update("location")} placeholder="City, State" />
                            <div className="md:col-span-2">
                                <label className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                                    Message
                                </label>
                                <textarea
                                    data-testid="dlg-message"
                                    rows={3}
                                    value={form.message}
                                    onChange={update("message")}
                                    className="w-full rounded-lg bg-background border hairline px-4 py-3 text-sm focus:outline-none focus:border-gold transition"
                                />
                            </div>
                            <div className="md:col-span-2 flex items-center justify-between gap-4">
                                <p className="text-xs text-muted-foreground">
                                    By submitting, you agree to be contacted about your project.
                                </p>
                                <button
                                    type="submit"
                                    disabled={busy}
                                    data-testid="dlg-submit"
                                    className="inline-flex items-center gap-2 h-11 px-6 rounded-full bg-gold text-white text-sm font-medium hover:brightness-110 transition disabled:opacity-50"
                                >
                                    {busy ? "Sending…" : "Book Now"}
                                    <ArrowRight className="h-4 w-4" />
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}

function Input({ label, testid, value, onChange, type = "text", required, placeholder }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {label}
            </span>
            <input
                type={type}
                value={value}
                onChange={onChange}
                required={required}
                placeholder={placeholder}
                data-testid={testid}
                className="w-full h-11 rounded-lg bg-background border hairline px-4 text-sm focus:outline-none focus:border-gold transition"
            />
        </label>
    );
}

function Sel({ label, testid, value, onChange }) {
    return (
        <label className="block">
            <span className="block text-xs uppercase tracking-widest text-muted-foreground mb-2">
                {label}
            </span>
            <select
                value={value}
                onChange={onChange}
                data-testid={testid}
                className="w-full h-11 rounded-lg bg-background border hairline px-3 text-sm focus:outline-none focus:border-gold transition"
            >
                <option value="">Select…</option>
                {PROJECT_TYPES.map((o) => (
                    <option key={o} value={o}>
                        {o}
                    </option>
                ))}
            </select>
        </label>
    );
}
